import React, { useState } from 'react';
import { X, Server, Check } from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import { i18n } from '../utils/i18n';

interface NodeReservationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPayment: () => void;
  user: any;
}

export function NodeReservationModal({ isOpen, onClose, onPayment, user }: NodeReservationModalProps) {
  const [nickname, setNickname] = useState(user?.nickname || '');
  const [referralCode, setReferralCode] = useState(user?.referralCode || '');
  const [phone, setPhone] = useState('');
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [isReservationComplete, setIsReservationComplete] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleReservation = async () => {
    if (!nickname || !phone) {
      toast.error('모든 필수 항목을 입력해주세요');
      return;
    }
    if (!agreeTerms) {
      toast.error('약관에 동의해주세요');
      return;
    }

    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('handy-jwt-token');
      const response = await fetch('http://159.223.63.184:3601/api/nodes/reservation-request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          productId: 1, // Validator Node
          nickname,
          referralCode,
          phone,
          agreed: agreeTerms
        })
      });

      const result = await response.json();
      
      if (result.success) {
        setIsReservationComplete(true);
        toast.success('노드 예약이 완료되었습니다!');
      } else {
        toast.error(result.error || '예약에 실패했습니다');
      }
    } catch (error) {
      console.error('예약 오류:', error);
      toast.error('예약 처리에 실패했습니다');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePayment = () => {
    if (!nickname || !phone) {
      toast.error('모든 필수 항목을 입력해주세요');
      return;
    }
    if (!agreeTerms) {
      toast.error('약관에 동의해주세요');
      return;
    }
    // PaymentModal로 전달
    onPayment();
    onClose();
    setIsReservationComplete(false);
  };

  const handleClose = () => {
    onClose();
    setIsReservationComplete(false);
    setNickname(user?.nickname || '');
    setPhone('');
    setAgreeTerms(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-background border border-border rounded-2xl max-w-sm w-full max-h-[80vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center">
              <Server className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="font-bold text-lg text-foreground">
                {isReservationComplete ? '예약 완료' : i18n.t('nodeSubscription')}
              </h2>
              <p className="text-sm text-muted-foreground">{i18n.t('handyFullNode')}</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="w-8 h-8 bg-muted rounded-full flex items-center justify-center hover:bg-accent transition-colors"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        {isReservationComplete ? (
          /* Reservation Complete */
          <div className="p-3 text-center">
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-green-600" />
            </div>
            <h3 className="font-bold text-xl text-foreground mb-2">Reservation Confirmed!</h3>
            <p className="text-muted-foreground text-sm mb-6">
              Your validator node has been reserved. Complete your payment to activate.
            </p>
            
            <div className="bg-muted rounded-xl p-4 mb-6">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground text-sm">Reservation ID</span>
                  <span className="font-mono text-sm">VN-{Date.now().toString().slice(-6)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground text-sm">Node Type</span>
                  <span className="text-sm font-medium">Handy Full Node</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground text-sm">Expected APY</span>
                  <span className="text-sm font-bold text-green-600">32%</span>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <button
                onClick={handleClose}
                className="w-full bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white py-4 rounded-2xl font-bold text-lg transition-all duration-300"
              >
                닫기
              </button>
            </div>
          </div>
        ) : (
          /* Reservation Form */
          <>
            {/* Product Summary */}
            <div className="p-3 border-b border-border">
              <h3 className="font-bold text-foreground mb-4">{i18n.t('productSummary')}</h3>
              <div className="bg-gradient-to-r from-orange-100 to-red-100 dark:from-orange-900/30 dark:to-red-900/30 rounded-xl p-4">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                    V
                  </div>
                  <div>
                    <h4 className="font-bold text-orange-800 dark:text-orange-200">{i18n.t('handyFullNode')}</h4>
                    <p className="text-orange-600 dark:text-orange-300 text-sm">{i18n.t('premiumValidator')}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white/50 dark:bg-black/20 rounded-lg p-3 text-center">
                    <p className="text-orange-700 dark:text-orange-300 text-xs mb-1">{i18n.t('stakingAmount')}</p>
                    <p className="font-bold text-orange-800 dark:text-orange-200">120,000 wHANDY</p>
                  </div>
                  <div className="bg-white/50 dark:bg-black/20 rounded-lg p-3 text-center">
                    <p className="text-orange-700 dark:text-orange-300 text-xs mb-1">{i18n.t('expectedAPY')}</p>
                    <p className="font-bold text-green-600">32%</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Reservation Form */}
            <div className="p-3 space-y-2">
              <div>
                <Label htmlFor="nickname" className="text-base font-medium text-foreground">{i18n.t('nickname')} *</Label>
                <Input
                  id="nickname"
                  value={nickname}
                  onChange={(e) => setNickname(e.target.value)}
                  placeholder="Enter your nickname"
                  disabled
                  className="bg-muted text-base h-10 mt-1 text-foreground font-medium"
                />
              </div>

              <div>
                <Label htmlFor="referralCode" className="text-base font-medium text-foreground">{i18n.t('myReferralCode')} *</Label>
                <Input
                  id="referralCode"
                  value={referralCode}
                  onChange={(e) => setReferralCode(e.target.value)}
                  placeholder={i18n.t('myReferralCode')}
                  disabled
                  className="bg-muted font-mono text-base h-10 mt-1 text-foreground font-medium"
                />
              </div>

              <div>
                <Label htmlFor="phone" className="text-base font-medium text-foreground">{i18n.t('phoneNumber')} *</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => {
                    // 숫자만 추출
                    const numbers = e.target.value.replace(/[^0-9]/g, '');
                    // 11자 제한
                    const limited = numbers.slice(0, 11);
                    // 자동 하이픈 추가
                    let formatted = limited;
                    if (limited.length > 7) {
                      formatted = `${limited.slice(0, 3)}-${limited.slice(3, 7)}-${limited.slice(7)}`;
                    } else if (limited.length > 3) {
                      formatted = `${limited.slice(0, 3)}-${limited.slice(3)}`;
                    }
                    setPhone(formatted);
                  }}
                  placeholder="010-0000-0000"
                  className="text-base h-10 mt-1 text-foreground font-medium"
                />
              </div>

              <div className="flex items-center space-x-3 pt-1">
                <Checkbox
                  id="terms"
                  checked={agreeTerms}
                  onCheckedChange={(checked) => setAgreeTerms(checked as boolean)}
                />
                <div className="flex-1">
                  <label htmlFor="terms" className="text-base text-foreground cursor-pointer font-medium">
                    {i18n.t('agreeToTerms')}
                  </label>
                </div>
              </div>

              <div className="space-y-2 pt-2">
                <button
                  onClick={handleReservation}
                  disabled={!nickname || !phone || !agreeTerms || isSubmitting}
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-white py-3 rounded-2xl font-bold text-base transition-all duration-300"
                >
                  {isSubmitting ? i18n.t('loading') : `📋 ${i18n.t('reserveNodeFree')}`}
                </button>
                
                <button
                  onClick={handlePayment}
                  disabled={!nickname || !phone || !agreeTerms || isSubmitting}
                  className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 disabled:opacity-50 disabled:cursor-not-allowed text-white py-3 rounded-2xl font-bold text-base transition-all duration-300"
                >
                  💳 결제하기 (120,000 wHANDY)
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}